import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { QueryParamsHandling, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { Image, ImageLoading } from 'ish-core/models/image/image.model';

/**
 * CUSTOMIZATION: Presidio — broken/missing product images fall back to the Presidio logo.
 */
@Component({
  selector: 'ish-product-image',
  templateUrl: './product-image.component.presidio.html',
  imports: [CommonModule, RouterModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductImageComponent implements OnInit {
  private translateService = inject(TranslateService);
  private context = inject(ProductContextFacade);
  private queryParamsHandlingInjector = inject<QueryParamsHandling>('PRODUCT_QUERY_PARAMS_HANDLING' as any, {
    optional: true,
  })!;

  /** Brand mark used when a remote product image fails to load. */
  readonly fallbackSrc = '/assets/themes/presidio/img/logo.svg';

  readonly imageType = input.required<string>();
  readonly imageView = input<string>(undefined);
  readonly link = input(false);
  readonly linkTarget = input<string>(undefined);
  readonly queryParamsHandling = input<QueryParamsHandling>('');
  readonly altText = input<string>(undefined);
  readonly loading = input<ImageLoading>('lazy');

  productURL$: Observable<string>;
  productImage$: Observable<Image>;
  defaultAltText$: Observable<string>;

  computedQueryParamsHandling: QueryParamsHandling;

  ngOnInit() {
    this.productURL$ = this.context.select('productURL');
    this.productImage$ = this.context.getProductImage$(this.imageType(), this.imageView());

    const imageView = this.imageView();
    this.defaultAltText$ = combineLatest([
      this.context.select('product').pipe(map(product => product?.name || product?.sku || '')),
      this.translateService.get('product.image.text.alttext'),
      of(imageView && `${imageView} ${this.imageType()}`),
    ]).pipe(map(parts => parts.filter(x => !!x).join(' ')));

    this.computedQueryParamsHandling = this.queryParamsHandlingInjector ?? this.queryParamsHandling();
  }

  /** Swap broken remote assets for the Presidio logo (once). */
  onImageError(event: Event) {
    const img = event.target as HTMLImageElement | null;
    if (!img || img.dataset.prsFallback === '1') {
      return;
    }
    img.dataset.prsFallback = '1';
    img.src = this.fallbackSrc;
    img.removeAttribute('srcset');
  }
}
